import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PendingAttemptsFlusher } from '@/components/assessments/PendingAttemptsFlusher';
import { GuestRoute, ProtectedRoute } from '@/components/ProtectedRoute';
import { RouteAccessGuard } from '@/components/RouteAccessGuard';
import { ContractPendingGuard } from '@/components/ContractPendingGuard';
import { LegalPolicyGuard } from '@/components/LegalPolicyGuard';
import { AppLayout } from '@/layouts/AppLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { AdministrationPage } from '@/features/admin/AdministrationPage';
import { CategoriesPage, CategoryFormPage } from '@/features/categories';
import {
  CoursesPage,
  CourseFormPage,
  CourseShowPage,
  CoursePreviewPage,
  CourseEditPage,
  CourseBuilderPage,
} from '@/features/courses';
import { CourseForumModerationPage } from '@/features/courses/CourseForumModerationPage';
import { CourseForumModerationThreadPage } from '@/features/courses/CourseForumModerationThreadPage';
import { LibraryPage, LibraryFormPage, LibraryPreviewPage, LibraryShowPage } from '@/features/library';
import { MediaPage } from '@/features/media/MediaPage';
import { MediaShowPage } from '@/features/media/MediaShowPage';
import {
  H5pPage,
  H5pLibrariesPage,
  H5pLibraryShowPage,
  H5pConstructorPage,
  H5pImportPage,
  H5pShowPage,
  H5pEditPage,
  H5pPreviewPage,
} from '@/features/h5p';
import { EvaluacionesPage, CreateAssessmentPage, AssessmentShowPage, AssessmentPreviewPage, QuizAttemptPage } from '@/features/assessments';
import { PracticalExamListPage } from '@/features/practical-exams/PracticalExamListPage';
import { PracticalExamSheetPage } from '@/features/practical-exams/PracticalExamSheetPage';
import { PracticalExamVehicleFormPage } from '@/features/practical-exams/PracticalExamVehicleFormPage';
import { PracticalExamVehiclesPage } from '@/features/practical-exams/PracticalExamVehiclesPage';
import {
  PreguntasPage,
  CreateQuestionPage,
  QuestionBanksPage,
  ImportQuestionsPage,
  LegacyXhtmlImportRedirect,
} from '@/features/questions';
import { CalificacionesPage, GradebookShowPage } from '@/features/grades';
import { ProgresoPage, ProgressShowPage } from '@/features/progress';
import { LearningSessionSettingsPage, LearningSessionTimePage, LearningSessionStudentDetailPage, CourseSessionTimePage } from '@/features/learning-sessions';
import { ExpedientePage, AcademicShowPage, MyExpedientePage } from '@/features/academic';
import { InscripcionesPage } from '@/features/enrollments/InscripcionesPage';
import { EnrollmentBulkPage } from '@/features/enrollments/EnrollmentBulkPage';
import { EnrollmentShowPage } from '@/features/enrollments/EnrollmentShowPage';
import { EnrollmentFormPage } from '@/features/enrollments/EnrollmentFormPage';
import { GruposPage } from '@/features/groups/GruposPage';
import { GroupFormPage } from '@/features/groups/GroupFormPage';
import { GroupShowPage } from '@/features/groups/GroupShowPage';
import { CredencialesPage } from '@/features/credentials/CredencialesPage';
import { CredentialIssuePage } from '@/features/credentials/CredentialIssuePage';
import { CredentialTemplatesPage } from '@/features/credentials/CredentialTemplatesPage';
import { CredentialTemplateEditPage, CredentialTemplateShowPage } from '@/features/credentials/CredentialTemplatePages';
import { CredentialShowPage } from '@/features/credentials/CredentialShowPage';
import { CredentialEditPage } from '@/features/credentials/CredentialEditPage';
import {
  ContratosPage,
  ContractTemplatesPage,
  ContractTemplateEditorPage,
  ContractShowPage,
  MisContratosPage,
} from '@/features/contracts';
import {
  LegalPoliciesPage,
  LegalPolicyEditorPage,
  LegalPolicyAcceptancesPage,
  LegalPolicyPage,
  AcceptLegalPoliciesPage,
} from '@/features/legal-policies';
import { UsersPage } from '@/features/users/UsersPage';
import { ImportUsersPage } from '@/features/users/ImportUsersPage';
import { UserFormPage } from '@/features/users/UserFormPage';
import { UserShowPage } from '@/features/users/UserShowPage';
import { RolesPage } from '@/features/roles/RolesPage';
import { RoleFormPage } from '@/features/roles/RoleFormPage';
import { RoleShowPage } from '@/features/roles/RoleShowPage';
import { PermissionsPage } from '@/features/permissions/PermissionsPage';
import { UserCustomFieldsPage } from '@/features/user-custom-fields/UserCustomFieldsPage';
import { UserCustomFieldFormPage } from '@/features/user-custom-fields/UserCustomFieldFormPage';
import { UserCustomFieldsPreviewPage } from '@/features/user-custom-fields/UserCustomFieldsPreviewPage';
import { UserCustomFieldFormOrderPage } from '@/features/user-custom-fields/UserCustomFieldFormOrderPage';
import { UserCustomFieldGroupsPage } from '@/features/user-custom-fields/UserCustomFieldGroupsPage';
import { UserCustomFieldGroupFormPage } from '@/features/user-custom-fields/UserCustomFieldGroupFormPage';
import { ServerStatusPage } from '@/features/development/ServerStatusPage';
import { AparienciaPage } from '@/features/appearance';
import { MisCursosPage } from '@/features/student/MisCursosPage';
import { CourseDetailPage } from '@/features/student/CourseDetailPage';
import { StudentCourseGradesPage } from '@/features/student/StudentCourseGradesPage';
import { CourseForumPage } from '@/features/student/CourseForumPage';
import { CourseForumThreadPage } from '@/features/student/CourseForumThreadPage';
import { CoursePlayPage } from '@/features/student/CoursePlayPage';
import { MisCertificadosPage } from '@/features/student/MisCertificadosPage';
import { CalendarPage } from '@/features/student/CalendarPage';
import { MisSoportePage } from '@/features/support/MisSoportePage';
import { SoporteTicketPage } from '@/features/support/SoporteTicketPage';
import { SoporteAdminPage } from '@/features/support/SoporteAdminPage';
import { SoporteAdminTicketPage } from '@/features/support/SoporteAdminTicketPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ForbiddenPage } from '@/features/errors/ForbiddenPage';
import { AdminLegacyRedirect } from '@/components/admin/AdminLegacyRedirect';
import { isPublicRegisterEnabled } from '@/config/auth-config';

const publicRegisterEnabled = isPublicRegisterEnabled();

export default function App() {
  return (
    <BrowserRouter>
      <PendingAttemptsFlusher />
      <Routes>
        <Route element={<GuestRoute />}>
          <Route path="/login" element={<LoginPage />} />
          {publicRegisterEnabled ? (
            <Route path="/register" element={<RegisterPage />} />
          ) : (
            <Route path="/register" element={<Navigate to="/login" replace />} />
          )}
          <Route path="/olvide-contrasena" element={<ForgotPasswordPage />} />
          <Route path="/restablecer-contrasena" element={<ResetPasswordPage />} />
          <Route path="/legal/:slug" element={<LegalPolicyPage />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route path="/acceso-denegado" element={<ForbiddenPage />} />
          <Route path="/403" element={<ForbiddenPage />} />

          <Route element={<AppLayout />}>
            <Route element={<RouteAccessGuard />}>
            <Route element={<LegalPolicyGuard />}>
            <Route element={<ContractPendingGuard />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/administracion" element={<AdministrationPage />} />
            <Route path="/admin/*" element={<AdminLegacyRedirect />} />

            <Route path="/categorias" element={<CategoriesPage />} />
            <Route path="/categorias/create" element={<CategoryFormPage />} />
            <Route path="/categorias/:id/edit" element={<CategoryFormPage />} />

            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/create" element={<CourseFormPage />} />
            <Route path="/courses/:id" element={<CourseShowPage />} />
            <Route path="/courses/:id/preview" element={<CoursePreviewPage />} />
            <Route path="/courses/:id/play/:item" element={<CoursePlayPage />} />
            <Route path="/courses/:id/edit" element={<CourseEditPage />} />
            <Route path="/courses/:id/builder" element={<CourseBuilderPage />} />
            <Route path="/courses/:id/horas-sesion" element={<CourseSessionTimePage />} />
            <Route path="/courses/:id/foro/:threadId" element={<CourseForumModerationThreadPage />} />
            <Route path="/courses/:id/foro" element={<CourseForumModerationPage />} />

            <Route path="/library" element={<LibraryPage />} />
            <Route path="/library/create" element={<LibraryFormPage />} />
            <Route path="/library/:id/edit" element={<LibraryFormPage />} />
            <Route path="/library/:id/preview" element={<LibraryPreviewPage />} />
            <Route path="/library/:id" element={<LibraryShowPage />} />

            <Route path="/media" element={<MediaPage />} />
            <Route path="/media/:id" element={<MediaShowPage />} />

            <Route path="/h5p" element={<H5pPage />} />
            <Route path="/h5p/constructor" element={<H5pConstructorPage />} />
            <Route path="/h5p/constructor/:id" element={<H5pEditPage />} />
            <Route path="/h5p/create" element={<H5pImportPage />} />
            <Route path="/h5p/libraries" element={<H5pLibrariesPage />} />
            <Route path="/h5p/libraries/:libraryId" element={<H5pLibraryShowPage />} />
            <Route path="/h5p/:id/preview" element={<H5pPreviewPage />} />
            <Route path="/h5p/:id/edit" element={<H5pEditPage />} />
            <Route path="/h5p/:id" element={<H5pShowPage />} />
            <Route path="/h5p/librerias" element={<Navigate to="/h5p/libraries" replace />} />

            <Route path="/evaluaciones" element={<EvaluacionesPage />} />
            <Route path="/evaluaciones/create" element={<CreateAssessmentPage />} />
            <Route path="/evaluaciones/intentos/:attemptId" element={<QuizAttemptPage />} />
            <Route path="/evaluaciones/:id/edit" element={<CreateAssessmentPage />} />
            <Route path="/evaluaciones/:id/preview" element={<AssessmentPreviewPage />} />
            <Route path="/evaluaciones/:id" element={<AssessmentShowPage />} />

            <Route path="/examen-practico/nuevo" element={<PracticalExamSheetPage />} />
            <Route path="/examen-practico/vehiculos/nuevo" element={<PracticalExamVehicleFormPage />} />
            <Route path="/examen-practico/vehiculos/:id" element={<PracticalExamVehicleFormPage />} />
            <Route path="/examen-practico/vehiculos" element={<PracticalExamVehiclesPage />} />
            <Route path="/examen-practico/:id" element={<PracticalExamSheetPage />} />
            <Route path="/examen-practico" element={<PracticalExamListPage />} />

            <Route path="/preguntas" element={<PreguntasPage />} />
            <Route path="/preguntas/create" element={<CreateQuestionPage />} />
            <Route path="/preguntas/bancos" element={<QuestionBanksPage />} />
            <Route path="/preguntas/importar" element={<ImportQuestionsPage />} />
            <Route path="/preguntas/importar/xhtml" element={<LegacyXhtmlImportRedirect />} />

            <Route path="/calificaciones" element={<CalificacionesPage />} />
            <Route path="/calificaciones/curso/:courseId" element={<GradebookShowPage />} />

            <Route path="/progreso" element={<ProgresoPage />} />
            <Route path="/progreso/configuracion-sesion" element={<LearningSessionSettingsPage />} />
            <Route path="/progreso/tiempo-conexion/:userId" element={<LearningSessionStudentDetailPage />} />
            <Route path="/progreso/tiempo-conexion" element={<LearningSessionTimePage />} />
            <Route path="/progreso/:userId/:courseId" element={<ProgressShowPage />} />

            <Route path="/expediente" element={<MyExpedientePage />} />
            {/* Admin: lista y transcripción (NO confundir con Mi expediente en /expediente) */}
            <Route path="/expediente/admin" element={<ExpedientePage />} />
            <Route path="/expediente/admin/:userId" element={<AcademicShowPage />} />

            <Route path="/inscripciones" element={<InscripcionesPage />} />
            <Route path="/inscripciones/bulk" element={<EnrollmentBulkPage />} />
            <Route path="/inscripciones/:id/edit" element={<EnrollmentFormPage />} />
            <Route path="/inscripciones/:id" element={<EnrollmentShowPage />} />

            <Route path="/grupos" element={<GruposPage />} />
            <Route path="/grupos/create" element={<GroupFormPage />} />
            <Route path="/grupos/:id/edit" element={<GroupFormPage />} />
            <Route path="/grupos/:id" element={<GroupShowPage />} />

            <Route path="/credenciales" element={<CredencialesPage />} />
            <Route path="/credenciales/emitir" element={<CredentialIssuePage />} />
            <Route path="/credenciales/plantillas" element={<CredentialTemplatesPage />} />
            <Route path="/credenciales/plantillas/:id/editor" element={<CredentialTemplateEditPage />} />
            <Route path="/credenciales/plantillas/:id" element={<CredentialTemplateShowPage />} />
            <Route path="/credenciales/:id/edit" element={<CredentialEditPage />} />
            <Route path="/credenciales/:id" element={<CredentialShowPage />} />

            <Route path="/contratos/plantillas/:id/editor" element={<ContractTemplateEditorPage />} />
            <Route path="/contratos/plantillas" element={<ContractTemplatesPage />} />
            <Route path="/contratos/:id" element={<ContractShowPage />} />
            <Route path="/contratos" element={<ContratosPage />} />

            <Route path="/politicas-legales/:id/editor" element={<LegalPolicyEditorPage />} />
            <Route path="/politicas-legales/aceptaciones" element={<LegalPolicyAcceptancesPage />} />
            <Route path="/politicas-legales" element={<LegalPoliciesPage />} />
            <Route path="/aceptar-politicas" element={<AcceptLegalPoliciesPage />} />

            <Route path="/usuarios" element={<UsersPage />} />
            <Route path="/usuarios/create" element={<UserFormPage />} />
            <Route path="/usuarios/importar" element={<ImportUsersPage />} />
            <Route path="/usuarios/:id/edit" element={<UserFormPage />} />
            <Route path="/usuarios/:id" element={<UserShowPage />} />

            <Route path="/roles" element={<RolesPage />} />
            <Route path="/roles/create" element={<RoleFormPage />} />
            <Route path="/roles/:id/edit" element={<RoleFormPage />} />
            <Route path="/roles/:id" element={<RoleShowPage />} />

            <Route path="/permisos" element={<PermissionsPage />} />

            <Route path="/campos-personalizados" element={<UserCustomFieldsPage />} />
            <Route path="/campos-personalizados/preview" element={<UserCustomFieldsPreviewPage />} />
            <Route path="/campos-personalizados/orden" element={<UserCustomFieldFormOrderPage />} />
            <Route path="/campos-personalizados/create" element={<UserCustomFieldFormPage />} />
            <Route path="/campos-personalizados/grupos" element={<UserCustomFieldGroupsPage />} />
            <Route path="/campos-personalizados/grupos/create" element={<UserCustomFieldGroupFormPage />} />
            <Route path="/campos-personalizados/grupos/:id/edit" element={<UserCustomFieldGroupFormPage />} />
            <Route path="/campos-personalizados/:id/edit" element={<UserCustomFieldFormPage />} />

            <Route path="/apariencia" element={<AparienciaPage />} />

            <Route path="/desarrollo/estado-del-servidor" element={<ServerStatusPage />} />

            <Route path="/mis-cursos" element={<MisCursosPage />} />
            <Route path="/calendario" element={<CalendarPage />} />
            <Route path="/soporte/admin/:id" element={<SoporteAdminTicketPage />} />
            <Route path="/soporte/admin" element={<SoporteAdminPage />} />
            <Route path="/soporte/:id" element={<SoporteTicketPage />} />
            <Route path="/soporte" element={<MisSoportePage />} />
            <Route path="/mis-cursos/:id" element={<CourseDetailPage />} />
            <Route path="/mis-cursos/:id/calificaciones" element={<StudentCourseGradesPage />} />
            <Route path="/mis-cursos/:id/foro/:threadId" element={<CourseForumThreadPage />} />
            <Route path="/mis-cursos/:id/foro" element={<CourseForumPage />} />
            <Route path="/mis-cursos/:id/play/:item" element={<CoursePlayPage />} />
            <Route path="/mis-certificados" element={<MisCertificadosPage />} />
            <Route path="/mis-certificados/insignias" element={<MisCertificadosPage />} />
            <Route path="/mis-contratos/:id" element={<MisContratosPage />} />
            <Route path="/mis-contratos" element={<MisContratosPage />} />
            <Route path="/mis-credenciales" element={<Navigate to="/mis-certificados" replace />} />
            <Route path="/mis-credenciales/*" element={<Navigate to="/mis-certificados" replace />} />
            <Route path="/profile" element={<ProfilePage />} />
            </Route>
            </Route>
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
